import { useState } from "react";
import type { Theme, FontPairing, Field } from "@structurify/shared-types";
import { MonoLabel, QuietButton } from "../components/primitives/index.ts";
import { createWorkflow } from "../api/workflows.ts";

interface Props {
  theme: Theme;
  fonts: FontPairing;
  fields: Field[];
  documentType: string | null | undefined;
  session: string | undefined;
  onFinish: () => void;
  onSaved: (workflowId: string) => void;
}

export function StageWorkflow({ theme, fonts, fields, documentType, session, onFinish, onSaved }: Props) {
  const defaultName = documentType ?? "Untitled workflow";
  const [name, setName] = useState(defaultName);
  const [advanced, setAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fieldCount = fields.length;
  const avgConfidence = fieldCount > 0
    ? Math.round((fields.reduce((s, f) => s + f.confidence, 0) / fieldCount) * 100)
    : 0;

  async function save() {
    if (!session || saving) return;
    setSaving(true);
    setError(null);
    try {
      const wf = await createWorkflow(session, name, {
        documentType: documentType ?? null,
        fieldCount,
        avgConfidence: avgConfidence / 100,
      });
      onSaved(wf.id);
      onFinish();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
      setSaving(false);
    }
  }

  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "clamp(20px, 4vw, 48px)", overflow: "auto" }}>
      <div style={{ width: "100%", maxWidth: 720, opacity: 0, animation: "fadeUp 600ms forwards ease-out" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 18, marginBottom: 10 }}>
          <MonoLabel theme={theme} fonts={fonts} size={10}>SAVE WORKFLOW</MonoLabel>
          <div style={{ flex: 1, height: 1, background: theme.rule }} />
          <MonoLabel theme={theme} fonts={fonts} size={10} color={theme.inkMute}>LEARNED FROM THIS DOCUMENT</MonoLabel>
        </div>

        <h2 style={{ fontFamily: fonts.headerFamily === "serif" ? fonts.serif : fonts.sans, fontWeight: fonts.headerWeight, fontSize: 38, lineHeight: 1.1, letterSpacing: fonts.headerTracking, color: theme.ink, margin: "12px 0 14px 0" }}>
          Process the next one in seconds.
        </h2>
        <p style={{ fontFamily: fonts.sans, fontSize: 15, color: theme.inkSoft, lineHeight: 1.55, margin: 0, marginBottom: 28, maxWidth: 540 }}>
          Structurify will recognize documents that look like this and extract the same fields automatically.
        </p>

        <div style={{ marginBottom: 24 }}>
          <MonoLabel theme={theme} fonts={fonts} size={9} style={{ display: "block", marginBottom: 8 }}>WORKFLOW NAME</MonoLabel>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ width: "100%", fontFamily: fonts.serif, fontSize: 24, fontWeight: 400, color: theme.ink, background: "transparent", border: "none", borderBottom: `1px solid ${theme.ruleStrong}`, padding: "8px 0", outline: "none" }}
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, background: theme.rule, border: `1px solid ${theme.rule}`, marginBottom: 28 }}>
          {[
            { l: "Document type", v: documentType ?? "—" },
            { l: "Fields learned", v: String(fieldCount) },
            { l: "Avg. confidence", v: fieldCount > 0 ? `${avgConfidence}%` : "—" },
          ].map((c, i) => (
            <div key={i} style={{ background: theme.panel, padding: "16px 18px" }}>
              <MonoLabel theme={theme} fonts={fonts} size={9} style={{ display: "block", marginBottom: 6 }}>{c.l.toUpperCase()}</MonoLabel>
              <div style={{ fontFamily: fonts.serif, fontSize: 22, color: theme.ink, fontWeight: 400 }}>{c.v}</div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 28 }}>
          <button
            onClick={() => setAdvanced(!advanced)}
            style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.inkSoft, background: "transparent", border: "none", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <svg width="9" height="9" viewBox="0 0 9 9" style={{ transform: advanced ? "rotate(90deg)" : "none", transition: "transform 200ms" }}>
              <path d="M3 2 L6 4.5 L3 7" stroke="currentColor" strokeWidth="1" fill="none" />
            </svg>
            Advanced — team access, API, custom fields
          </button>
          {advanced && (
            <div style={{ marginTop: 14, padding: "16px 18px", background: theme.panel, border: `1px solid ${theme.rule}`, fontFamily: fonts.sans, fontSize: 13, color: theme.inkSoft, lineHeight: 1.7 }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                <span>API endpoint</span>
                <span style={{ fontFamily: fonts.mono, fontSize: 11, color: theme.ink }}>POST /v1/extract — coming soon</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                <span>Custom prompt or computed field</span>
                <span style={{ color: theme.inkMute }}>Coming soon</span>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div style={{ marginBottom: 16, fontFamily: fonts.sans, fontSize: 13, color: theme.confidenceLow }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <QuietButton theme={theme} fonts={fonts} primary onClick={() => { void save(); }} disabled={saving || !session}>
            {saving ? "Saving…" : "Save workflow"}
          </QuietButton>
          <QuietButton theme={theme} fonts={fonts} onClick={onFinish}>Process more documents</QuietButton>
        </div>

        {!session && (
          <p style={{ marginTop: 12, fontFamily: fonts.sans, fontSize: 12, color: theme.inkMute }}>
            Sign in to save workflows.
          </p>
        )}
      </div>
    </div>
  );
}
